import React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import PropTypes from 'prop-types';
import { Loader } from 'semantic-ui-react';
import { FieldErrors } from '@weezevent/nacre';
import i18n from 'i18next';
import GalleryModal from '../../components/gallery';
import useAppContext from '../../context/useAppContext';

// TinyMCE so the global var exists
import 'tinymce/tinymce';
// DOM model
import 'tinymce/models/dom/model';
// Theme
import 'tinymce/themes/silver';
// Toolbar icons
import 'tinymce/icons/default';
// Editor styles
import 'tinymce/skins/ui/oxide/skin.css';

// importing the plugin js.
// if you use a plugin that is not listed here the editor will fail to load
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/help';
import 'tinymce/plugins/help/js/i18n/keynav/en';
import 'tinymce/plugins/image';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/media';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/wordcount';

// importing plugin resources
import 'tinymce/plugins/emoticons/js/emojis';

// Content styles, including inline UI like fake cursors
// Commented for ui purposes, tinymce classes overrides willy's
// import 'tinymce/skins/content/default/content.css';
// import 'tinymce/skins/ui/oxide/content.css';

/**
 * Display a tiny editor component
 * @param {*} param0
 */
export default function EditorComponent({ description, onChange, placeholder, height = 400, fontFamily = 'Graphik', menubar = true, errors = null, block_formats = null }) {
    const [loading, setLoading] = React.useState(true);
    const [editor, setEditor] = React.useState(null);
    const [openGallery, setOpenGallery] = React.useState(false);
    const locale = i18n.language;
    const [{ organization }] = useAppContext();

    const _locale = React.useMemo(() => {
        let [lang] = locale.split('-');

        return lang === 'fr' ? 'fr_FR' : lang;
    }, [locale]);

    const onSelected = React.useCallback(
        data => {
            editor.execCommand('mceInsertContent', false, '<img alt="image" width="200" src="' + data + '"/>');
            setOpenGallery(false);
        },
        [editor]
    );

    return (
        <div className={`editor-container ${errors ? 'editor-error' : ''}`} style={{ height: `${height + (errors ? 20 : 0)}px` }}>
            <div style={{ visibility: loading ? 'visible' : 'hidden' }} className="loading">
                <Loader active />
            </div>
            <GalleryModal
                apiKeyUnsplash={window.weezConfig.unsplash}
                onSelected={onSelected}
                onUpload={onSelected}
                organization={organization}
                isOpenModal={openGallery}
                onClose={() => setOpenGallery(false)}
            />

            <Editor
                style={{ visibility: !loading ? 'visible' : 'hidden' }}
                value={description}
                licenseKey={window.weezConfig.api.tiny_editor_key}
                onInit={() => setLoading(false)}
                init={{
                    placeholder: { placeholder },
                    height: height,
                    menubar: menubar,
                    language: _locale,
                    content_style: `body { font-family:${fontFamily}; font-size:14px }`,
                    paste_block_drop: true,
                    plugins: [
                        'advlist',
                        'autolink',
                        'lists',
                        'link',
                        'charmap',
                        'preview',
                        'anchor',
                        'searchreplace',
                        'visualblocks',
                        'code',
                        'image',
                        'fullscreen',
                        'insertdatetime',
                        'media',
                        'table',
                        'code',
                        'help',
                        'wordcount'
                    ],
                    resize: false,
                    block_formats: block_formats,
                    toolbar:
                        'bold italic underline strikethrough forecolor fontsize  alignleft aligncenter alignright alignjustify indent outdent numlist bullist  customImageButton media table link  undo redo code preview  blocks fontfamily',
                    font_formats:
                        'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Graphik=graphik; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
                    setup: editor => {
                        setEditor(editor);
                        editor.ui.registry.addButton('customImageButton', {
                            icon: 'image',
                            onAction: () => setOpenGallery(true)
                        });
                    }
                }}
                onEditorChange={onChange}
            />
            {errors && <FieldErrors errors={errors} />}
        </div>
    );
}

EditorComponent.propTypes = {
    description: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    fontFamily: PropTypes.string,
    menubar: PropTypes.bool,
    height: PropTypes.number,
    errors: PropTypes.array
};
